/**
 * Admin · Share Links
 *
 * Mint stable public share URLs for any of the admin's ready audio
 * journeys. Each entry becomes a /share/audio/<slug> page anyone can
 * open without signing in.
 *
 * Route: /admin/share-links (admin-only)
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Plus, Copy, Trash2, Check, ExternalLink } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` });

// Convert any string into a friendly slug — used to auto-suggest a slug
// when the admin arrives from "My Journeys → Share".
const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);

export default function AdminShareLinks() {
  const [search] = useSearchParams();
  const [items, setItems] = useState(null);
  const [journeys, setJourneys] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    slug: "", title: "", subtitle: "", byline: "", journey_id: "",
  });
  const [copied, setCopied] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    setItems(null);
    try {
      const [a, b] = await Promise.all([
        fetch(`${API}/api/admin/share-links`, { headers: auth() }),
        fetch(`${API}/api/admin/share-links/_journeys`, { headers: auth() }),
      ]);
      if (a.ok) setItems((await a.json()).items || []);
      else setItems([]);
      if (b.ok) setJourneys((await b.json()).items || []);
    } catch (_) { setItems([]); }
  };
  useEffect(() => { load(); }, []);

  // Pre-fill the create form when arriving from "My Journeys → Share".
  // We accept `journey_id` and an optional `title` query param and use
  // them to seed the slug + heading so the admin doesn't have to retype
  // anything they already know.
  useEffect(() => {
    const jid = search.get("journey_id");
    const titleHint = search.get("title");
    if (!jid && !titleHint) return;
    setForm((f) => ({
      ...f,
      journey_id: jid || f.journey_id,
      title: f.title || titleHint || "",
      slug: f.slug || slugify(titleHint || jid || ""),
    }));
  }, [search]);

  const submit = async (e) => {
    e?.preventDefault();
    if (creating) return;
    setCreating(true);
    setErr(null);
    try {
      const r = await fetch(`${API}/api/admin/share-links`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify(form),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setErr(d.detail || `HTTP ${r.status}`);
      } else {
        setForm({ slug: "", title: "", subtitle: "", byline: "", journey_id: "" });
        load();
      }
    } catch (e) { setErr(e.message); } finally { setCreating(false); }
  };

  const del = async (slug) => {
    if (!window.confirm(`Revoke /share/audio/${slug}? Anyone with the link will get a 'no longer active' page.`)) return;
    await fetch(`${API}/api/admin/share-links/${slug}`, { method: "DELETE", headers: auth() });
    load();
  };

  const copy = async (slug) => {
    const url = `${window.location.origin}/share/audio/${slug}`;
    try { await navigator.clipboard.writeText(url); } catch (_) { /* clipboard may be blocked */ }
    setCopied(slug);
    setTimeout(() => setCopied(null), 1800);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0B0805] to-[#080604] text-white">
      <main className="px-5 pt-16 pb-16 max-w-2xl mx-auto">
        <h1 className="text-3xl font-light text-amber-50/95 mb-1" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
          Share Links
        </h1>
        <p className="text-white/45 text-xs italic mb-6">Mint a stable public URL for any ready audio journey.</p>

        {/* Create form */}
        <form onSubmit={submit} className="rounded-2xl bg-white/[0.03] ring-1 ring-white/8 p-5 space-y-3 mb-8" data-testid="share-create-form">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Slug" placeholder="sam-night-3hr" value={form.slug} onChange={(v) => setForm((f) => ({ ...f, slug: v }))} required testid="share-slug" />
            <Field label="Heading" placeholder="Night Installation · 3-Hour Loop" value={form.title} onChange={(v) => setForm((f) => ({ ...f, title: v }))} required testid="share-title" />
          </div>
          <Field label="Subtitle" placeholder="For Sam · made by Michael" value={form.subtitle} onChange={(v) => setForm((f) => ({ ...f, subtitle: v }))} testid="share-subtitle" />
          <Field label="Byline" placeholder="Loop this through the night. Truth becoming identity." value={form.byline} onChange={(v) => setForm((f) => ({ ...f, byline: v }))} testid="share-byline" />
          <label className="block">
            <span className="text-amber-200/75 text-[10px] uppercase tracking-[0.22em]">Pick a journey</span>
            <select
              value={form.journey_id}
              onChange={(e) => setForm((f) => ({ ...f, journey_id: e.target.value }))}
              data-testid="share-journey"
              className="mt-1 w-full bg-white/[0.04] ring-1 ring-white/10 rounded-lg px-3 py-2.5 text-sm text-white/95 focus:outline-none focus:ring-amber-400/40"
            >
              <option value="">— select a ready journey —</option>
              {journeys.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.name} · {j.duration_minutes}min · {j.id}
                </option>
              ))}
            </select>
          </label>
          {err && <p className="text-red-300 text-xs">{err}</p>}
          <button type="submit" disabled={creating} data-testid="share-create-submit" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-400/95 hover:bg-amber-300 text-[#0B0805] text-sm font-medium disabled:opacity-50">
            {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Create share link
          </button>
        </form>

        {/* List */}
        {items === null ? (
          <div className="flex justify-center py-10 text-white/45"><Loader2 size={20} className="animate-spin" /></div>
        ) : items.length === 0 ? (
          <p className="text-white/45 text-sm italic text-center py-8">No share links yet. Create one above.</p>
        ) : (
          <div className="space-y-2" data-testid="share-list">
            {items.map((it) => {
              const url = `${window.location.origin}/share/audio/${it.slug}`;
              return (
                <div key={it.slug} className="rounded-xl bg-white/[0.025] ring-1 ring-white/8 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-amber-50/95 text-sm font-medium">{it.title}</div>
                      {it.subtitle && <div className="text-white/55 text-[11px] italic mt-0.5">{it.subtitle}</div>}
                      <a href={url} target="_blank" rel="noreferrer" className="block mt-2 text-[11px] text-amber-200/80 hover:text-amber-100 truncate">
                        <ExternalLink size={10} className="inline -mt-0.5 mr-1" />{url}
                      </a>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button onClick={() => copy(it.slug)} title="Copy URL" data-testid={`share-copy-${it.slug}`}
                        className="p-2 rounded-full ring-1 ring-white/15 hover:bg-white/[0.06] text-white/70">
                        {copied === it.slug ? <Check size={14} className="text-emerald-300" /> : <Copy size={14} />}
                      </button>
                      <button onClick={() => del(it.slug)} title="Revoke" data-testid={`share-delete-${it.slug}`}
                        className="p-2 rounded-full ring-1 ring-red-300/20 hover:bg-red-500/10 text-red-300/85">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, required, testid }) {
  return (
    <label className="block">
      <span className="text-amber-200/75 text-[10px] uppercase tracking-[0.22em]">{label}</span>
      <input
        type="text" value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} required={required}
        data-testid={testid}
        className="mt-1 w-full bg-white/[0.04] ring-1 ring-white/10 rounded-lg px-3 py-2.5 text-sm text-white/95 focus:outline-none focus:ring-amber-400/40 placeholder-white/25"
      />
    </label>
  );
}
